// Customize mode — the shared visual pieces (inline-styled so every surface
// renders them identically: platform Run view, builder preview, downloaded
// runtime). Violet marks everything that belongs to the mode, kept apart from
// the app's own accent so nobody mistakes it for data entry.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  Check, GripVertical, Loader2, MoreVertical, PencilRuler, Redo2, RotateCcw, Settings, SlidersHorizontal, Undo2, X,
} from 'lucide-react'
import type { CustomizeSession } from './session'
import { tw } from '../testHooks'

export const VIOLET = '#6d28d9'
export const VIOLET_DARK = '#4c1d95'
export const VIOLET_SOFT = '#ede9fe'
export const VIOLET_LINE = '#c4b5fd'

// ── the ⋮ menu ───────────────────────────────────────────────────────────

const MENU_W = 280

export function CustomizeMenu({ enabled, onCustomize, onReset, customizedCount = 0, subtitle }: {
  enabled: boolean
  onCustomize: () => void
  onReset?: () => void
  customizedCount?: number
  /** One line under "Customize" (what it arranges on this screen). */
  subtitle?: string
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const ref = useRef<HTMLButtonElement | null>(null)
  useLayoutEffect(() => {
    if (!open || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8)) })
  }, [open])
  if (!enabled) return null
  return (
    <>
      <button ref={ref} type="button" title="More" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} {...tw('action', 'more')} style={iconTrigger}>
        <MoreVertical size={15} />
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9460 }} onClick={() => setOpen(false)}>
          <div role="menu" onClick={(e) => e.stopPropagation()}
            style={{ ...menuBox, width: MENU_W, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}>
            <button type="button" role="menuitem" {...tw('action', 'customize-screen')}
              onClick={() => { setOpen(false); onCustomize() }}
              style={{ ...menuItem, background: '#f5f3ff', alignItems: 'flex-start', padding: 10 }}>
              <span style={{
                width: 28, height: 28, borderRadius: 7, background: VIOLET, color: '#fff', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><SlidersHorizontal size={15} /></span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600, color: VIOLET_DARK }}>Customize</span>
                {subtitle && <span style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.4 }}>{subtitle}</span>}
              </span>
            </button>
            {onReset && (
              <button type="button" role="menuitem" disabled={!customizedCount}
                onClick={() => { setOpen(false); onReset() }}
                style={{ ...menuItem, opacity: customizedCount ? 1 : 0.5 }}>
                <RotateCcw size={14} />
                <span style={{ flex: 1 }}>Reset to standard</span>
                {customizedCount > 0 && <span style={{ fontSize: 13, color: VIOLET }}>{customizedCount} change{customizedCount === 1 ? '' : 's'}</span>}
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

// ── the bar across the top of a screen in the mode ───────────────────────

export interface CustomizeView { id: string; label: string }

export function CustomizeBar({
  title, session, views, view, onView, onDone, resetOptions, actions, scope = 'org', toolsRow, note, subtitle,
}: {
  title: string
  /** Replaces the scope's standard line under the title (a website says
   *  what it changes in its own words). */
  subtitle?: string
  session: CustomizeSession
  views?: CustomizeView[]
  view?: string
  onView?: (id: string) => void
  onDone: () => void
  /** "Reset…" choices — e.g. this view / everything. Empty ⇒ no Reset… */
  resetOptions: { label: string; run: () => void; count?: number }[]
  /** Extra entries rendered before Done (e.g. "Additional attributes"). */
  actions?: ReactNode
  /** 'org' = Customize (this organization's overlay); 'app' = Make changes
   *  (the screen itself, for everyone). */
  scope?: 'org' | 'app'
  /** A second row of tools (Make changes: Checks before saving, More
   *  actions, Ask AI to change), right-aligned under the first. */
  toolsRow?: ReactNode
  /** A standing note under the bar's first row (e.g. read-only). */
  note?: ReactNode
}) {
  const [resetOpen, setResetOpen] = useState(false)
  const st = session.status
  const busy = st === 'saving' || st === 'loading'
  const bar = (
    <div role="region" aria-label={scope === 'app' ? 'Make changes' : 'Customize'}
      {...tw('screen', scope === 'app' ? 'make-changes-bar' : 'customize-bar')} style={{
      flexShrink: 0, background: VIOLET_DARK, color: '#fff', padding: '8px 16px',
      display: 'flex', flexDirection: 'column', gap: 8,
      position: 'relative', zIndex: 5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {scope === 'app' ? <PencilRuler size={17} /> : <SlidersHorizontal size={17} />}
        <span style={{ fontWeight: 600, fontSize: 15.5 }}>{scope === 'app' ? 'Make changes' : 'Customize'} · {title}</span>
        <span style={{ fontSize: 13.5, color: '#ddd6fe' }}>
          {subtitle ?? (scope === 'app'
            ? 'changes the screen for everyone · every change saves on its own'
            : 'for your organization only · every change saves on its own')}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13.5, color: '#ddd6fe' }}>
          {st === 'saving' || st === 'loading'
            ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />Saving…</>
            : st === 'error'
              ? <span style={{ color: '#fecaca' }}>Not saved</span>
              : st === 'saved' ? <><Check size={13} color="#86efac" />Saved</> : null}
        </span>
        <button type="button" style={barBtnState(!session.canUndo || busy)} disabled={!session.canUndo || busy}
          title={session.undoLabel ? `Undo: ${session.undoLabel}` : 'Nothing to undo'}
          onClick={() => void session.undo()} {...tw('action', 'customize-undo')}>
          <Undo2 size={14} /> Undo
        </button>
        <button type="button" style={barBtnState(!session.canRedo || busy)} disabled={!session.canRedo || busy}
          title={session.redoLabel ? `Redo: ${session.redoLabel}` : 'Nothing to redo'}
          onClick={() => void session.redo()} {...tw('action', 'customize-redo')}>
          <Redo2 size={14} /> Redo
        </button>
        {resetOptions.length > 0 && <span style={{ position: 'relative' }}>
          <button type="button" style={barBtn} onClick={() => setResetOpen((v) => !v)}
            aria-haspopup="menu" aria-expanded={resetOpen} {...tw('action', 'customize-reset')}>
            Reset…
          </button>
          {resetOpen && (
            <div role="menu" style={{ ...menuBox, position: 'absolute', right: 0, top: 36, width: 250 }}
              onMouseLeave={() => setResetOpen(false)}>
              {resetOptions.map((o) => (
                <button key={o.label} type="button" role="menuitem" style={menuItem}
                  disabled={o.count === 0}
                  onClick={() => { setResetOpen(false); o.run() }}>
                  <RotateCcw size={14} /><span style={{ flex: 1 }}>{o.label}</span>
                  {o.count != null && <span style={{ fontSize: 13, color: VIOLET }}>{o.count}</span>}
                </button>
              ))}
            </div>
          )}
        </span>}
        {actions}
        <button type="button" style={{ ...barBtn, background: '#fff', color: VIOLET_DARK, borderColor: '#fff', fontWeight: 600 }}
          onClick={onDone} {...tw('action', 'customize-done')}>Done</button>
      </div>
      {toolsRow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {toolsRow}
        </div>
      )}
      {note && (
        <div role="status" style={{
          padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,.12)', fontSize: 14, lineHeight: 1.45,
        }}>{note}</div>
      )}
      {session.warnings.length > 0 && session.errors.length === 0 && (
        <div role="status" {...tw('msg', 'warning')} style={{
          padding: '8px 12px', borderRadius: 8, background: '#fff7ed', color: '#7c2d12', fontSize: 14, lineHeight: 1.45,
        }}>{session.warnings.join(' · ')}</div>
      )}
      {views && views.length > 1 && (
        <div role="tablist" aria-label="What you are customizing" style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: 3, borderRadius: 8,
          background: 'rgba(255,255,255,.1)', alignSelf: 'flex-start', flexWrap: 'wrap',
        }}>
          {views.map((v) => {
            const on = v.id === view
            return (
              <button key={v.id} type="button" role="tab" aria-selected={on}
                onClick={() => onView?.(v.id)} {...tw('action', `customize-view:${v.id}`)}
                style={{
                  height: 28, padding: '0 12px', borderRadius: 6, border: 0, cursor: 'pointer',
                  font: `${on ? 600 : 500} 13px inherit`, fontFamily: 'inherit',
                  background: on ? '#fff' : 'transparent', color: on ? VIOLET_DARK : '#ddd6fe',
                }}>{v.label}</button>
            )
          })}
        </div>
      )}
      {session.errors.length > 0 && (
        <div role="alert" {...tw('msg', 'error')} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 8,
          background: '#fff', color: '#7f1d1d', fontSize: 14, lineHeight: 1.45,
        }}>
          <span style={{ flex: 1 }}>
            <b style={{ fontWeight: 600 }}>Not saved. </b>
            {session.errors.join(' · ')}
          </span>
          <button type="button" aria-label="Dismiss" onClick={session.dismissErrors}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#7f1d1d' }}><X size={14} /></button>
        </div>
      )}
    </div>
  )
  return bar
}

// ── small affordances ────────────────────────────────────────────────────

export function Grip({ size = 13, title = 'Drag to move' }: { size?: number; title?: string }) {
  return (
    <span title={title} aria-hidden="true" style={{ color: '#7c3aed', display: 'inline-flex', cursor: 'grab', flexShrink: 0 }}>
      <GripVertical size={size} />
    </span>
  )
}

export function GearButton({ label, onClick, active, style }: {
  label: string
  onClick: (el: HTMLElement) => void
  active?: boolean
  style?: CSSProperties
}) {
  return (
    <button type="button" aria-label={label} title={label}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(e.currentTarget) }}
      onMouseDown={(e) => e.stopPropagation()}
      draggable={false}
      {...tw('action', `customize:${label}`)}
      style={{
        width: 24, height: 24, borderRadius: 5, padding: 0, cursor: 'pointer', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${active ? VIOLET : '#ddd6fe'}`,
        background: active ? VIOLET : '#fff', color: active ? '#fff' : VIOLET,
        ...style,
      }}><Settings size={13} /></button>
  )
}

export function CustomizedDot({ title = 'Customized for your organization' }: { title?: string }) {
  return <span title={title} style={{ width: 7, height: 7, borderRadius: '50%', background: VIOLET, display: 'inline-block', flexShrink: 0 }} />
}

export function Switch({ on, onChange, label, disabled }: {
  on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean
}) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={() => onChange(!on)} {...tw('field', `customize-switch:${label}`)}
      style={{
        width: 40, height: 22, borderRadius: 11, border: 0, padding: 2, flexShrink: 0,
        display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start',
        background: disabled ? '#e5e7eb' : on ? VIOLET : '#d5dae1',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      }}>
      <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)' }} />
    </button>
  )
}

// ── an anchored panel (the ⚙ popover shell) ──────────────────────────────

export function AnchoredPanel({ anchor, onClose, width = 400, children, label }: {
  anchor: HTMLElement | null
  onClose: () => void
  width?: number
  label: string
  children: ReactNode
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor?.getBoundingClientRect()
      const h = boxRef.current?.offsetHeight || 420
      const vw = window.innerWidth
      const vh = window.innerHeight
      if (!r) { setPos({ top: 80, left: Math.max(8, (vw - width) / 2) }); return }
      let left = r.right + 8
      if (left + width > vw - 8) left = Math.max(8, r.left - width - 8)
      // An anchor scrolled above the window still opens the panel in sight.
      let top = Math.max(8, r.top)
      if (top + h > vh - 8) top = Math.max(8, vh - h - 8)
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [anchor, width])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  if (typeof document === 'undefined') return null
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9470 }} onMouseDown={onClose}>
      <div ref={boxRef} role="dialog" aria-label={label} onMouseDown={(e) => e.stopPropagation()}
        {...tw('screen', `customize-panel:${label}`)}
        style={{
          position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width,
          maxHeight: 'calc(100vh - 16px)', overflowY: 'auto',
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
          border: '1px solid #ddd6fe', borderRadius: 12,
          boxShadow: '0 20px 50px rgba(76,29,149,.22)', fontSize: 14.5,
        }}>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export function PanelSection({ title }: { title: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700,
      color: VIOLET, textTransform: 'uppercase', letterSpacing: '.07em', paddingTop: 4,
    }}>
      {title}<span style={{ flex: 1, height: 1, background: VIOLET_SOFT }} />
    </div>
  )
}

export function PanelRow({ title, hint, children, dim }: {
  title: string; hint?: string; children: ReactNode; dim?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 38, opacity: dim ? 0.55 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontWeight: 500 }}>{title}</span>
        {hint && <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  )
}

/** A text input that saves on blur / Enter, with an optional ↺ to standard. */
export function PanelText({ label, value, placeholder, onCommit, standard, onReset, customized, disabled }: {
  label: string
  value: string
  placeholder?: string
  onCommit: (v: string) => void
  standard?: string
  onReset?: () => void
  customized?: boolean
  disabled?: boolean
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
        <span>{label}</span>
        {customized && onReset
          ? <button type="button" onClick={(e) => { e.preventDefault(); onReset() }}
              style={{ border: 0, background: 'transparent', color: VIOLET, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <RotateCcw size={12} />{standard ? `Standard: ${standard}` : 'Reset'}
            </button>
          : standard ? <span style={{ fontWeight: 400, color: 'var(--color-text-secondary, #6b7280)' }}>Standard: {standard}</span> : null}
      </span>
      <input value={draft} placeholder={placeholder} disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft !== value) onCommit(draft) }}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
        {...tw('field', `customize-text:${label}`)}
        style={{
          height: 34, boxSizing: 'border-box', width: '100%', borderRadius: 6, padding: '0 10px',
          border: `1px solid ${customized ? '#7c3aed' : 'var(--color-border, #d5dae1)'}`,
          font: '400 14px inherit', fontFamily: 'inherit',
          background: disabled ? 'var(--color-bg-secondary, #f1f3f6)' : 'var(--color-bg, #fff)',
          color: 'var(--color-text, #111827)',
        }} />
    </label>
  )
}

export function PanelHeader({ title, sub, saved }: { title: string; sub?: string; saved?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 16px 10px', borderBottom: '1px solid var(--color-border, #f1f3f6)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
        {sub && <span style={{ font: "400 12px 'IBM Plex Mono', ui-monospace, monospace", color: 'var(--color-text-secondary, #6b7280)' }}>{sub}</span>}
      </div>
      {saved && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#15803d' }}><Check size={13} />Saved</span>}
    </div>
  )
}

export function PanelNote({ tone = 'info', children }: { tone?: 'info' | 'warn'; children: ReactNode }) {
  const warn = tone === 'warn'
  return (
    <div role={warn ? 'alert' : undefined} style={{
      padding: '9px 12px', borderRadius: 8, fontSize: 13.5, lineHeight: 1.45,
      background: warn ? '#fff7ed' : 'var(--color-bg-secondary, #f1f3f6)',
      border: warn ? '1px solid #fdba74' : '1px solid transparent',
      color: warn ? '#7c2d12' : 'var(--color-text, #374151)',
    }}>{children}</div>
  )
}

// ── the tray of hidden things ────────────────────────────────────────────

export function HiddenTray({ title, items, hint, onDropHide }: {
  title: string
  /** Default button caption for the items ("Show" / "Add"). */
  action?: string
  items: { key: string; label: string; action?: string; onShow: () => void }[]
  hint?: string
  /** Something was dropped on the tray (drag a field / header here to hide it). */
  onDropHide?: (dragData: string) => void
}) {
  const [over, setOver] = useState(false)
  return (
    <div aria-label={title} {...tw('screen', `customize-tray:${title}`)}
      onDragOver={onDropHide ? (e) => { e.preventDefault(); setOver(true) } : undefined}
      onDragLeave={() => setOver(false)}
      onDrop={onDropHide ? (e) => {
        e.preventDefault(); setOver(false)
        const d = e.dataTransfer.getData('text/x-twasta-customize')
        if (d) onDropHide(d)
      } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 16px',
        borderTop: `1px dashed ${over ? '#7c3aed' : '#9aa3af'}`,
        background: over ? '#f5f3ff' : 'var(--color-bg-secondary, #f8f9fb)', flexShrink: 0,
      }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
      {items.length === 0 && <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary, #6b7280)' }}>None</span>}
      {items.map((it) => (
        <span key={it.key} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, height: 30, padding: '0 4px 0 10px',
          border: '1px dashed #9aa3af', borderRadius: 15, background: 'var(--color-bg, #fff)', fontSize: 14,
        }}>
          {it.label}
          <button type="button" onClick={it.onShow} {...tw('action', `customize-show:${it.key}`)}
            style={{ height: 22, padding: '0 8px', borderRadius: 11, border: 0, background: VIOLET_SOFT, color: '#5b21b6', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            {it.action || 'Show'}
          </button>
        </span>
      ))}
      {hint && <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary, #6b7280)' }}>{hint}</span>}
    </div>
  )
}

/** Drag payload helpers (HTML5 DnD; one MIME type for the whole mode). */
export const DRAG_MIME = 'text/x-twasta-customize'
export function setDragData(e: React.DragEvent, data: string) {
  e.dataTransfer.setData(DRAG_MIME, data)
  e.dataTransfer.effectAllowed = 'move'
}
export function getDragData(e: React.DragEvent): string {
  return e.dataTransfer.getData(DRAG_MIME)
}

// ── styles ───────────────────────────────────────────────────────────────

const iconTrigger: CSSProperties = {
  width: 30, height: 30, padding: 0, borderRadius: 6, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: '1px solid transparent', background: 'transparent', color: 'var(--color-text, #111827)',
}
const menuBox: CSSProperties = {
  position: 'fixed', padding: 6, borderRadius: 8, zIndex: 9461,
  background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e2e6ec)',
  boxShadow: '0 8px 24px rgba(0,0,0,.18)', display: 'flex', flexDirection: 'column',
}
const menuItem: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
  padding: '8px 10px', borderRadius: 6, border: 0, background: 'transparent', cursor: 'pointer',
  font: '400 13px inherit', fontFamily: 'inherit', color: 'var(--color-text, #111827)',
}
const barBtn: CSSProperties = {
  height: 30, padding: '0 12px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid rgba(255,255,255,.3)', background: 'transparent', color: '#fff',
  font: '500 13px inherit', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
}
/** A bar button that looks unavailable when it is (nothing to undo / redo). */
const barBtnState = (disabled: boolean): CSSProperties =>
  disabled ? { ...barBtn, opacity: 0.4, cursor: 'default' } : barBtn

/** An extra entry for the Customize bar (its `actions` slot), drawn like the
 *  bar's own buttons — e.g. "Additional attributes". */
export function CustomizeBarAction({ icon, label, onClick, hook, badge }: {
  icon?: ReactNode
  label: string
  onClick: () => void
  /** The test hook (`tw('action', hook)`). */
  hook?: string
  /** A small pill after the label ("New"). */
  badge?: string
}) {
  return (
    <button type="button" style={{ ...barBtn, borderColor: '#fff', fontWeight: 600 }}
      onClick={onClick} {...tw('action', hook)}>
      {icon}{label}
      {badge && (
        <span style={{
          fontSize: 13, fontWeight: 700, lineHeight: 1.2,
          background: '#fff', color: VIOLET_DARK, borderRadius: 4, padding: '0 6px',
        }}>{badge}</span>
      )}
    </button>
  )
}

// ── Make changes: choice chips, panel tabs, a plain select ───────────────

/** A row of choice chips (one pressed). */
export function ChipGroup<T extends string | number>({ label, options, value, onChange, hook, disabled }: {
  label: string
  options: { value: T; label: string }[]
  value: T | null | undefined
  onChange: (v: T) => void
  hook?: string
  disabled?: boolean
}) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((o) => {
        const on = o.value === value
        return (
          <button key={String(o.value)} type="button" role="radio" aria-checked={on} disabled={disabled}
            onClick={() => onChange(o.value)} {...tw('action', `${hook || 'chip'}:${o.value}`)}
            style={{
              minHeight: 30, padding: '0 12px', borderRadius: 15, cursor: disabled ? 'default' : 'pointer',
              border: `1px solid ${on ? VIOLET : VIOLET_LINE}`, background: on ? VIOLET : '#fff',
              color: on ? '#fff' : VIOLET_DARK, fontSize: 13.5, fontWeight: on ? 600 : 500, fontFamily: 'inherit',
              opacity: disabled ? 0.55 : 1,
            }}>{o.label}</button>
        )
      })}
    </div>
  )
}

/** The ⚙ panel's tabs (Basics / Rules / When it changes / Checks). */
export function PanelTabs<T extends string>({ tabs, value, onChange }: {
  tabs: { id: T; label: string; count?: number }[]
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div role="tablist" aria-label="Field settings" style={{
      display: 'flex', flexWrap: 'wrap', gap: 2, padding: '8px 12px 0', borderBottom: `1px solid ${VIOLET_SOFT}`,
    }}>
      {tabs.map((t) => {
        const on = t.id === value
        return (
          <button key={t.id} type="button" role="tab" aria-selected={on} onClick={() => onChange(t.id)}
            {...tw('action', `make-changes-tab:${t.id}`)}
            style={{
              height: 34, padding: '0 12px', border: 0, cursor: 'pointer', background: 'transparent',
              borderBottom: `2px solid ${on ? VIOLET : 'transparent'}`, marginBottom: -1,
              color: on ? VIOLET_DARK : 'var(--color-text-secondary, #4b5563)', fontSize: 14,
              fontWeight: on ? 600 : 500, fontFamily: 'inherit',
            }}>
            {t.label}{t.count ? <span style={{ fontWeight: 500, color: VIOLET }}> {t.count}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

/** A labelled select in the panel's look. */
export function PanelSelect({ label, value, options, onChange, disabled, hint }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  disabled?: boolean
  hint?: string
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        {...tw('field', `make-changes-select:${label}`)}
        style={{
          height: 34, boxSizing: 'border-box', borderRadius: 6, padding: '0 8px', fontFamily: 'inherit',
          border: '1px solid var(--color-border, #d5dae1)', fontSize: 14,
          background: disabled ? 'var(--color-bg-secondary, #f1f3f6)' : 'var(--color-bg, #fff)',
          color: 'var(--color-text, #111827)',
        }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint && <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>{hint}</span>}
    </label>
  )
}

/** A sentence already in place, with Remove ("⚡ Fill in …", "✓ …"). */
export function RuleLine({ icon, text, sub, onRemove }: {
  icon: ReactNode; text: string; sub?: string; onRemove?: () => void
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px', borderRadius: 8,
      background: '#f5f3ff', border: `1px solid ${VIOLET_SOFT}`, fontSize: 14,
    }}>
      <span aria-hidden="true" style={{ color: VIOLET, flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span>{text}</span>
        {sub && <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>{sub}</span>}
      </span>
      {onRemove && (
        <button type="button" onClick={onRemove} {...tw('action', 'make-changes-remove-rule')}
          style={{ border: 0, background: 'transparent', color: '#b42318', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
          Remove
        </button>
      )}
    </div>
  )
}

/** The violet primary button the panels use (Done / Add). */
export function VioletButton({ children, onClick, disabled, hook, quiet }: {
  children: ReactNode; onClick: () => void; disabled?: boolean; hook?: string; quiet?: boolean
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} {...tw('action', hook)}
      style={{
        height: 32, padding: '0 14px', borderRadius: 6, cursor: disabled ? 'default' : 'pointer',
        border: quiet ? `1px solid ${VIOLET_LINE}` : 0, background: quiet ? '#fff' : VIOLET,
        color: quiet ? VIOLET_DARK : '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 6, opacity: disabled ? 0.55 : 1,
      }}>{children}</button>
  )
}

/** A centred dialog in the mode's look (Add a field, Checks before saving,
 *  the More actions dialogs). Esc / the backdrop close it. */
export function CenterDialog({ title, onClose, children, footer, width = 720, hook }: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: number
  hook?: string
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  if (typeof document === 'undefined') return null
  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9475, background: 'rgba(28,36,48,.35)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflowY: 'auto',
    }} onMouseDown={onClose}>
      <div role="dialog" aria-label={title} onMouseDown={(e) => e.stopPropagation()}
        {...tw('screen', hook || `make-changes-dialog:${title}`)}
        style={{
          width: '100%', maxWidth: width, background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
          borderRadius: 14, boxShadow: '0 24px 60px rgba(76,29,149,.28)', display: 'flex', flexDirection: 'column',
          fontSize: 14.5,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border, #f1f3f6)' }}>
          <strong style={{ flex: 1, fontSize: 16 }}>{title}</strong>
          <button type="button" aria-label="Close" onClick={onClose}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--color-text-secondary, #4b5563)', display: 'inline-flex' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
        {footer && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', padding: '12px 18px', borderTop: '1px solid var(--color-border, #f1f3f6)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** ✎ Make changes — beside a screen's header actions, for people who build
 *  the app. Opens the mode with the 'app' scope. */
export function MakeChangesButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} title="Change this screen for everyone who uses the app"
      {...tw('action', 'make-changes')}
      style={{
        height: 30, padding: '0 10px', borderRadius: 6, cursor: 'pointer', flexShrink: 0,
        border: `1px solid ${VIOLET_LINE}`, background: '#f5f3ff', color: VIOLET_DARK,
        font: '600 13px inherit', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      <PencilRuler size={14} /> Make changes
    </button>
  )
}
